/**
 * Generic deep-merge for admin config defaults. Plain objects merge
 * recursively; arrays/primitives in `source` replace target values.
 * `base` must be a plain object — arrays, class instances, Maps, and
 * Sets are not supported and will throw.
 *
 * Use this to apply published-config overrides onto bundled defaults.
 * The returned object never aliases nested subtrees from `base`, so
 * callers may freely mutate it without corrupting the defaults.
 */
export function mergeDefaults<T extends object>(
  base: T,
  source: unknown,
): T {
  if (!isPlainObject(base)) {
    throw new TypeError('mergeDefaults: base must be a plain object');
  }
  const cloned = JSON.parse(JSON.stringify(base)) as Record<string, unknown>;
  if (!isPlainObject(source)) return cloned as T;
  for (const k of Object.keys(source)) {
    const sv = source[k];
    const tv = cloned[k];
    if (isPlainObject(sv) && isPlainObject(tv)) {
      cloned[k] = mergeDefaults(tv, sv);
    } else if (sv !== undefined) {
      cloned[k] = sv;
    }
  }
  return cloned as T;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
